import React, { useState, useEffect } from 'react';
import { useUserPermissions } from '../../hooks/useUserPermissions';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { 
  Toolbar, 
  Divider, 
  List, 
  IconButton, 
  Badge, 
  CircularProgress, 
  Tooltip,
  Snackbar,
  Alert,
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography
} from '@mui/material';
import Box from '@mui/material/Box';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import Header from '../../components/header';
import DashboardIcon from '@mui/icons-material/Dashboard';
import AddIcon from '@mui/icons-material/Add';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import NotificationsIcon from '@mui/icons-material/Notifications';
import PeopleIcon from '@mui/icons-material/People';
import SettingsIcon from '@mui/icons-material/Settings';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import AssignmentIcon from '@mui/icons-material/Assignment';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import SortIcon from '@mui/icons-material/Sort';
import ViewKanbanIcon from '@mui/icons-material/ViewKanban';
import ViewListIcon from '@mui/icons-material/ViewList';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';

import DateRangeIcon from '@mui/icons-material/DateRange';
import PersonIcon from '@mui/icons-material/Person';
import CommentIcon from '@mui/icons-material/Comment';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import Board from '../../components/board';
import UnifiedUserManagement from '../../components/unifiedUserManagement';

import { PermissionBadge } from '../../components/permissionBadge';
import { useBoardPermissions } from '../../hooks/useBoardPermissions';

// import EditBoardModal from '../../components/editBoardModal'; - Não é mais necessário, usaremos o CreateBoardModal
import { Board as BoardInterface, Card, Column } from '../../interfaces/board';
import { Project, ProjectMember } from '../../interfaces/project';
import { useAuth } from '../../contexts/AuthContext';
import { projectService } from '../../services/project';
import CreateBoardModal from '../../components/createBoardModal';
import { format, parseISO, isPast, isToday } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useToast } from '../../contexts/ToastContext';

import {
  ProjectContainer,
  MainContent,
  AppBar,
  AppBarContent,
  ProjectTitle,
  HeaderActions,
  HeaderButton,
  HeaderIconButton,
  MenuDrawer,
  DrawerHeader,
  ProjectSection,
  ProjectName,
  ProjectDescription,
  SectionTitle,
  BoardList,
  BoardItem,
  BoardItemIcon,
  BoardItemText,
  AddBoardButton,
  ContentArea,
  KanbanBoardContainer,
  BoardHeader,
  BoardTitleArea,
  BoardTitleText,
  BoardDescription,
  BoardControls,
  ColumnsContainer,
  Column as StyledColumn,
  ColumnHeader,
  ColumnTitle,
  ColumnCount,
  ColumnContent,
  Card as StyledCard,
  CardTitle,
  CardContent,
  CardDescription,
  CardLabels,
  CardLabel,
  CardFooter,
  CardMeta,
  CardMetaItem,
  CardDueDate,
  CardAssignee,
  AssigneeAvatar,
  PriorityBadge,
  AddButton,
  AddColumnButton,
  EmptyBoard,
  EmptyBoardIcon,
  EmptyBoardTitle,
  EmptyBoardText
} from './styles';

const ProjectPage: React.FC = () => {
  const { projectId } = useParams<{projectId: string}>();
  const navigate = useNavigate();
  const location = useLocation();
  const { showError, showSuccess } = useToast();
  
  // Obter parâmetros do location.state
  const initialBoardId = location.state?.initialBoardId;
  const openCardId = location.state?.openCardId;
  const adminOverride = location.state?.adminOverride;
  
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [loading, setLoading] = useState(true);
  const [project, setProject] = useState<Project | null>(null);
  const [boards, setBoards] = useState<BoardInterface[]>([]);
  const [activeBoard, setActiveBoard] = useState<BoardInterface | null>(null);
  const [createBoardModalOpen, setCreateBoardModalOpen] = useState(false);
  const [createCardModalOpen, setCreateCardModalOpen] = useState(false);
  const [activeColumnId, setActiveColumnId] = useState<string | null>(null);
  const [editCardModalOpen, setEditCardModalOpen] = useState(false);
  const [selectedCard, setSelectedCard] = useState<Card | null>(null);

  const [userManagementModalOpen, setUserManagementModalOpen] = useState(false);

  // Usar o hook de permissões de usuário
  const permissions = useUserPermissions(project);
  
  // Estado para o menu do board
  const [boardMenuAnchor, setBoardMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedBoardId, setSelectedBoardId] = useState<string | null>(null);
  const boardMenuOpen = Boolean(boardMenuAnchor);
  const [deleteBoardConfirmOpen, setDeleteBoardConfirmOpen] = useState(false);
  const [boardToDelete, setBoardToDelete] = useState<{id: string, title: string} | null>(null);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [boardToEdit, setBoardToEdit] = useState<BoardInterface | null>(null);
  
  // Chave para forçar o recarregamento do componente Board quando atualizado
  const [boardKey, setBoardKey] = useState<number>(0);
  
  const { user } = useAuth();
  
  // Efeito para abrir o card quando openCardId está presente
  useEffect(() => {
    const openCardFromUrl = async () => {
      if (openCardId) {
        try {
          // Buscar detalhes do card
          const card = await projectService.getCardById(openCardId);
          
          if (card) {
            // Usa o columnId do estado de navegação se existir
            const columnId = location.state?.columnId || card.columnId;
            
            // Se o card não estiver no board atual, navegue para o board correto
            const boardToLoad = boards.find(board => 
              board.columns.some(column => column.id === columnId)
            );
            
            if (boardToLoad && (!activeBoard || activeBoard.id !== boardToLoad.id)) {
              // Carregar os detalhes do board
              const boardDetails = await projectService.getBoardById(boardToLoad.id);
              setActiveBoard(boardDetails);
              
              // Incrementar a chave para forçar o recarregamento do componente Board
              setBoardKey(prev => prev + 1);
            }
            
            // Definir o card para abrir
            setSelectedCard(card);
            setEditCardModalOpen(true);
            
            // Limpar o estado de navegação após usar
            window.history.replaceState(null, '', location.pathname);
          }
        } catch (error) {
          console.error('Erro ao carregar card:', error);
        }
      }
    };

    // Executar se tiver boards carregados
    if (boards.length > 0) {
      openCardFromUrl();
    }
  }, [boards, openCardId, activeBoard, location.state]);
  
  // Método para abrir o modal de criação
  const handleOpenCreateBoardModal = () => {
    setModalMode('create');
    setCreateBoardModalOpen(true);
  };
  
  // Fetch project and boards on mount
  useEffect(() => {
    const fetchProjectData = async () => {
      if (!projectId) return;
      
      setLoading(true);
      try {
        // Primeiro, obteremos os dados do projeto
        const projectData = await projectService.getProjectById(projectId);
        setProject(projectData);
        
        // Permissões do usuário são verificadas automaticamente pelo hook useUserPermissions
        
        // Agora, buscaremos os quadros do projeto
        try {
          const boardsData = await projectService.getBoardsByProjectId(projectId);
          setBoards(boardsData);
          
          // Verificar se há um initialBoardId para seleção inicial ou um boardId vindo do estado de navegação
          const navBoardId = location.state?.boardId || initialBoardId;
          
          if (navBoardId) {
            // Procurar o board na lista
            const selectedBoard = boardsData.find(board => board.id === navBoardId);
            if (selectedBoard) {
              // Carregar detalhes completos do board selecionado
              try {
                const boardDetails = await projectService.getBoardById(selectedBoard.id);
                setActiveBoard(boardDetails);
                // Incrementar a chave para forçar o recarregamento do componente Board
                setBoardKey(prev => prev + 1);
                
                // Limpar o estado de navegação após usar
                if (location.state?.boardId) {
                  window.history.replaceState(null, '', location.pathname);
                }
              } catch (error) {
                console.error('Erro ao carregar o quadro selecionado:', error);
                // Fallback para o primeiro board se não conseguir carregar o board selecionado
                setActiveBoard(boardsData[0]);
              }
            } else {
              // Se não encontrar o board selecionado, usar o primeiro
              setActiveBoard(boardsData.length > 0 ? boardsData[0] : null);
            }
          } else {
            // Comportamento padrão: Set the first board as active if exists
            if (boardsData.length > 0) {
              setActiveBoard(boardsData[0]);
            }
          }
        } catch (boardError: any) {
          console.error('Erro ao carregar quadros do projeto:', boardError);
          if (boardError.response?.status === 404) {
            showError('Não foi possível carregar os quadros. Endpoint não encontrado: verifique as rotas da API.');
          } else {
            showError(`Erro ao carregar quadros: ${boardError.message || 'Erro desconhecido'}`);
          }
        }
      } catch (projectError: any) {
        console.error('Erro ao carregar dados do projeto:', projectError);
        showError(`Não foi possível carregar os dados do projeto. ${projectError.message || 'Tente novamente mais tarde.'}`);
      } finally {
        setLoading(false);
      }
    };

    fetchProjectData();
  }, [projectId, initialBoardId]);

  // A função checkIfUserIsAdmin foi substituída pelo hook useUserPermissions

  const handleOpenUserManagement = () => {
    setUserManagementModalOpen(true);
  };

  const handleCloseUserManagement = () => {
    setUserManagementModalOpen(false);
  };



  const handleUserManagementSuccess = () => {
    // Recarregar os dados do projeto para atualizar a lista de membros
    if (projectId) {
      projectService.getProjectById(projectId).then(data => {
        setProject(data);
        showSuccess('Usuário gerenciado com sucesso!');
      });
    }
  };

  // Funções para gerenciar o menu do board
  const handleBoardMenuOpen = (event: React.MouseEvent<HTMLElement>, boardId: string) => {
    event.stopPropagation(); // Evita que o clique dispare a seleção do board
    setBoardMenuAnchor(event.currentTarget);
    setSelectedBoardId(boardId);
    
  };
  
  const handleBoardMenuClose = () => {
    setBoardMenuAnchor(null);
    setSelectedBoardId(null);
  };
  
  const handleDeleteBoardConfirm = () => {
    // Encontra o board pelo ID selecionado e salva as informações para exibir no modal
    if (selectedBoardId) {
      const board = boards.find(b => b.id === selectedBoardId);
      if (board) {
        setBoardToDelete({
          id: board.id,
          title: board.title
        });
        // Fecha o menu de opções
        handleBoardMenuClose();
        // Abre o modal de confirmação
        setDeleteBoardConfirmOpen(true);
      }
    }
  };
  
  const handleEditBoard = () => {
    // Encontra o board pelo ID selecionado
    if (selectedBoardId) {
      const board = boards.find(b => b.id === selectedBoardId);
      if (board) {
        setBoardToEdit(board);
        // Fecha o menu de opções
        handleBoardMenuClose();
        // Abre o modal no modo de edição
        setModalMode('edit');
        setCreateBoardModalOpen(true);
      }
    }
  };
  
  const handleBoardUpdated = async () => {
    
    if (!projectId) return;
    
    try {
      // Recarrega a lista de boards do projeto
      const boardsData = await projectService.getBoardsByProjectId(projectId);
      setBoards(boardsData);
      
      // Se o board editado for o ativo, atualiza o board ativo e incrementa a chave
      // para forçar o recarregamento do componente
      if (boardToEdit && activeBoard && boardToEdit.id === activeBoard.id) {
        const updatedBoard = boardsData.find(b => b.id === boardToEdit.id);
        if (updatedBoard) {
          setActiveBoard(updatedBoard);
          // Incrementa a chave para forçar o recarregamento do componente Board
          setBoardKey(prev => prev + 1);
        }
      }
      
      showSuccess('Quadro atualizado com sucesso!');
    } catch (error) {
      console.error('Erro ao recarregar dados dos quadros após edição:', error);
      showError('O quadro foi editado, mas não foi possível atualizar a exibição. Por favor, atualize a página.');
    } finally {
      setBoardToEdit(null);
    }
  };
  
  const handleConfirmDialogClose = () => {
    setDeleteBoardConfirmOpen(false);
    setBoardToDelete(null);
  };
  
  const handleDeleteBoard = async () => {
    if (!boardToDelete) return;
    
    try {
      // Fechamos o modal de confirmação
      handleConfirmDialogClose();
      
      // Atualizar o estado local imediatamente para feedback visual rápido
      const updatedBoards = boards.filter(board => board.id !== boardToDelete.id);
      setBoards(updatedBoards);
      
      // Se o board que está sendo excluído for o ativo, limpar o board ativo
      if (activeBoard && activeBoard.id === boardToDelete.id) {
        setActiveBoard(updatedBoards.length > 0 ? updatedBoards[0] : null);
      }
      
      // Chamar a API para excluir o board
      await projectService.deleteBoard(boardToDelete.id);
      
      showSuccess('Quadro excluído com sucesso!');
      
    } catch (error) {
      console.error("Erro ao excluir quadro:", error);
      // Recarregar a lista de boards para garantir que o estado está sincronizado com o backend
      if (projectId) {
        try {
          const boardsData = await projectService.getBoardsByProjectId(projectId);
          setBoards(boardsData);
          
          // Atualiza o board ativo ou define como null se não houver boards
          if (boardsData.length > 0) {
            setActiveBoard(boardsData[0]);
          } else {
            setActiveBoard(null);
          }
        } catch (refreshError) {
          console.error("Erro ao recarregar dados dos quadros após falha na exclusão:", refreshError);
        }
      }
      
      showError('Erro ao excluir quadro. Tente novamente mais tarde.');
    }
  };

  const handleDrawerToggle = () => {
    setSidebarOpen(!sidebarOpen);
  };
  const handleBoardClick = async (board: BoardInterface) => {
    try {
      setLoading(true);
      // Carregamos os detalhes completos do quadro selecionado
      const boardDetails = await projectService.getBoardById(board.id);
      setActiveBoard(boardDetails);
      // Incrementa a chave para forçar o recarregamento do componente Board
      setBoardKey(prev => prev + 1);
    } catch (error) {
      console.error('Erro ao carregar o quadro:', error);
      showError('Não foi possível carregar os detalhes do quadro. Tente novamente mais tarde.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoBack = () => {
    navigate('/hub');
  };

  const handleOpenActivityDashboard = () => {
    navigate(`/admin/activities?projectId=${projectId}`);
  };

  const handleBoardCreated = (boardId: string) => {
    // Após criar um novo quadro, atualizamos a lista de quadros
    const fetchUpdatedBoards = async () => {
      if (!projectId) return;
      
      try {
        const boardsData = await projectService.getBoardsByProjectId(projectId);
        setBoards(boardsData);
        
        // Encontra o novo quadro na lista atualizada
        const newBoard = boardsData.find(board => board.id === boardId);
        if (newBoard) {
          setActiveBoard(newBoard);
          // Incrementa a chave para forçar o recarregamento do componente Board
          setBoardKey(prev => prev + 1);
        }
        
        showSuccess('Quadro criado com sucesso!');
      } catch (error) {
        console.error('Erro ao atualizar a lista de quadros:', error);
        showError('O quadro foi criado, mas não foi possível atualizar a lista. Por favor, atualize a página.');
      }
    };
    
    fetchUpdatedBoards();
  };

  const handleAddCard = (columnId: string) => {
    setActiveColumnId(columnId);
    setCreateCardModalOpen(true);
  };

  const handleCreateCard = async (cardData: any) => {
    if (!activeColumnId) return;
    
    try {
      // O serviço já atualiza o card na API
      const newCard = await projectService.createCard(activeColumnId, cardData);
      
      // Atualizar o activeBoard com o novo card
      if (activeBoard) {
        // Atualiza o board para incluir o novo card
        setActiveBoard(prevBoard => {
          if (!prevBoard) return null;
          
          return {
            ...prevBoard,
            columns: prevBoard.columns.map(column => {
              if (column.id === activeColumnId) {
                return {
                  ...column,
                  cards: [...column.cards, newCard]
                };
              }
              return column;
            })
          };
        });
      }
      
      showSuccess('Cartão criado com sucesso!');
    } catch (error) {
      console.error('Erro ao criar cartão:', error);
      showError('Não foi possível criar o cartão. Tente novamente mais tarde.');
    } finally {
      setCreateCardModalOpen(false);
    }
  };

  const handleCardClick = (card: Card) => {
    setSelectedCard(card);
    setEditCardModalOpen(true);
  };

  const handleUpdateCard = async (cardId: string, updatedData: Partial<Card>) => {
    try {
      // O serviço já atualiza o card na API
      const updatedCard = await projectService.updateCard(cardId, updatedData);
      
      // Atualizar o activeBoard com o card atualizado
      if (activeBoard) {
        setActiveBoard(prevBoard => {
          if (!prevBoard) return null;
          
          return {
            ...prevBoard,
            columns: prevBoard.columns.map(column => {
              return {
                ...column,
                cards: column.cards.map(card => {
                  if (card.id === cardId) {
                    return {
                      ...card,
                      ...updatedCard
                    };
                  }
                  return card;
                })
              };
            })
          };
        });
      }
      
      showSuccess('Cartão atualizado com sucesso!');
    } catch (error) {
      console.error('Erro ao atualizar cartão:', error);
      showError('Não foi possível atualizar o cartão. Tente novamente mais tarde.');
    } finally {
      setEditCardModalOpen(false);
      setSelectedCard(null);
    }
  };

  const handleDeleteCard = async (cardId: string) => {
    try {
      // O serviço já exclui o card na API
      await projectService.deleteCard(cardId);
      
      // Atualizar o activeBoard removendo o card excluído
      if (activeBoard) {
        setActiveBoard(prevBoard => {
          if (!prevBoard) return null;
          
          return {
            ...prevBoard,
            columns: prevBoard.columns.map(column => {
              return {
                ...column,
                cards: column.cards.filter(card => card.id !== cardId)
              };
            })
          };
        });
      }
      
      showSuccess('Cartão excluído com sucesso!');
    } catch (error) {
      console.error('Erro ao excluir cartão:', error);
      showError('Não foi possível excluir o cartão. Tente novamente mais tarde.');
    } finally {
      setEditCardModalOpen(false);
      setSelectedCard(null);
    }
  };

  const renderBoardContent = () => {
    if (!activeBoard) {
      return (
        <EmptyBoard>
          <EmptyBoardIcon>
            <DashboardIcon style={{ fontSize: 48 }} />
          </EmptyBoardIcon>
          <EmptyBoardTitle variant="h5">
            Nenhum quadro selecionado
          </EmptyBoardTitle>
          <EmptyBoardText>
            {permissions.canEdit
              ? "Selecione um quadro existente ou crie um novo para começar a organizar seu projeto."
              : "Selecione um quadro existente para começar a trabalhar no projeto."}
          </EmptyBoardText>
          
          {permissions.canEdit && (
            <HeaderButton
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleOpenCreateBoardModal}
            >
              Criar novo quadro
            </HeaderButton>
          )}
        </EmptyBoard>
      );
    }

    return (
      <KanbanBoardContainer backgroundColor={activeBoard.backgroundColor}>
        <BoardHeader>
          <BoardTitleArea>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <BoardTitleText>{activeBoard.title}</BoardTitleText>
              {/* Badge de permissão do usuário neste board */}
              {
                (() => {
                  // Determinar o nível de acesso do projeto
                  let projectAccessLevel = 'Observer';
                  if (permissions.isAdmin) {
                    projectAccessLevel = 'Administrator';
                  } else if (!permissions.isObserver) {
                    projectAccessLevel = 'Member';
                  }
                  
                  // Determinar o nível de acesso efetivo para este board
                  let effectiveAccessLevel = projectAccessLevel;
                  
                  // Se o usuário tem acesso direto ao board, usar essa permissão
                  if (activeBoard.hasDirectAccess && activeBoard.userAccessLevel) {
                    effectiveAccessLevel = activeBoard.userAccessLevel;
                  }
                  
                  return <PermissionBadge accessLevel={effectiveAccessLevel} size="medium" />;
                })()
              }
            </Box>
            {activeBoard.description && (
              <BoardDescription>{activeBoard.description}</BoardDescription>
            )}
          </BoardTitleArea>
          
          <BoardControls>
            {/* <Tooltip title="Filtrar cards">
            <Tooltip title="Filtrar cards">
              <HeaderIconButton>
                <FilterListIcon />
              </HeaderIconButton>
            </Tooltip>
            
            <Tooltip title="Ordenar">
              <HeaderIconButton>
                <SortIcon />
              </HeaderIconButton>
            </Tooltip>
            
            <Tooltip title="Alternar visualização">
              <HeaderIconButton>
                <ViewKanbanIcon />
              </HeaderIconButton>
            </Tooltip>
            
            <Tooltip title="Mais opções">
              <HeaderIconButton>
                <MoreVertIcon />
              </HeaderIconButton>
            </Tooltip> */}
          </BoardControls>
        </BoardHeader>
        
        {/* Componente Board integrado com a API - Usando a chave para forçar recarregamento */}
        <Board key={boardKey} id={activeBoard.id} />
      </KanbanBoardContainer>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <ProjectContainer>
      <Header 
        title={project?.title || project?.name || 'Projeto'}
        showBackButton={true}
        showMenuToggle={true}
        menuOpen={sidebarOpen}
        onMenuToggle={handleDrawerToggle}
        isAdmin={permissions.isAdmin}
        isMember={!permissions.isAdmin && !permissions.isObserver}
        isObserver={permissions.isObserver}
        showUserType={false}
        isSystemAdmin={project?.currentUserIsSystemAdmin}
        actionButton={
          <Box sx={{ display: 'flex', gap: 1 }}>
            {(permissions.isAdmin || project?.currentUserIsSystemAdmin) && (
              <>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<AssignmentIcon />}
                  onClick={handleOpenActivityDashboard}
                  size="small"
                >
                  Painel de Atividades
                </Button>
                {permissions.isAdmin && (
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<ManageAccountsIcon />}
                    onClick={handleOpenUserManagement}
                    size="small"
                  >
                    Gerenciar Usuários
                  </Button>
                )}
              </>
            )}
          </Box>
        }
      />
      
      <MenuDrawer
        variant="permanent"
        open={sidebarOpen}
      >
        <Toolbar />
        <ProjectSection open={sidebarOpen}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
            <ProjectName>{project?.title || project?.name || 'Projeto'}</ProjectName>
            {/* Informação de tipo de usuário removida da barra lateral para evitar duplicação com o header */}
          </Box>
          <ProjectDescription>
            {project?.description || 'Sem descrição'}
          </ProjectDescription>
        </ProjectSection>
        
        <SectionTitle open={sidebarOpen}>Quadros</SectionTitle>
        <BoardList open={sidebarOpen}>
          {boards.map((board) => {
            // Determinar o nível de acesso do projeto
            let projectAccessLevel = 'Observer';
            if (permissions.isAdmin) {
              projectAccessLevel = 'Administrator';
            } else if (!permissions.isObserver) {
              projectAccessLevel = 'Member';
            }
            
            // Determinar o nível de acesso efetivo para este board
            let effectiveAccessLevel = projectAccessLevel;
            let showBadge = true; // Sempre mostrar badge para consistência
            
            // Se o usuário tem acesso direto ao board E é diferente do acesso do projeto
            if (board.hasDirectAccess && board.userAccessLevel) {
              effectiveAccessLevel = board.userAccessLevel;
              // Só destacar o badge se a permissão for diferente da permissão do projeto
              // Isso ajuda a identificar quando alguém tem permissão especial em um board específico
            }
            
            return (
              <BoardItem
                key={board.id}
                active={activeBoard?.id === board.id}
                onClick={() => handleBoardClick(board)}
              >
                <BoardItemIcon color={activeBoard?.id === board.id ? '#2196f3' : 'rgba(255, 255, 255, 0.3)'} />
                <BoardItemText>{board.title}</BoardItemText>
                {permissions.canEdit && (
                  <IconButton 
                    size="small" 
                    onClick={(e) => handleBoardMenuOpen(e, board.id)}
                    sx={{ padding: '4px', color: 'rgba(255, 255, 255, 0.7)' }}
                  >
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                )}
              </BoardItem>
            );
          })}
        </BoardList>
        
        <AddBoardButton
          startIcon={<AddIcon />}
          onClick={handleOpenCreateBoardModal}
          open={sidebarOpen}
          sx={{ display: permissions.canEdit ? 'flex' : 'none' }}
        >
          Adicionar quadro
        </AddBoardButton>
        
        <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.1)', my: 2 }} />
        
        <List>
          {/* Outros itens do menu podem ser adicionados aqui */}
        </List>
      </MenuDrawer>
      
      <MainContent isSidebarOpen={sidebarOpen}>
        <Toolbar /> {/* Espaçamento para o AppBar */}
        <ContentArea>
          {renderBoardContent()}
        </ContentArea>
      </MainContent>
      
      {/* Modais */}
      <CreateBoardModal
        open={createBoardModalOpen}
        projectId={projectId || ''}
        onClose={() => {
          setCreateBoardModalOpen(false);
          // Reset do estado do modal
          setModalMode('create');
          setBoardToEdit(null);
        }}
        onBoardCreated={handleBoardCreated}
        onBoardUpdated={handleBoardUpdated}
        editMode={modalMode === 'edit'}
        boardData={boardToEdit || undefined}
        title={modalMode === 'edit' ? 'Editar Quadro' : 'Criar Quadro'}
      />
      
      <UnifiedUserManagement
        open={userManagementModalOpen}
        onClose={handleCloseUserManagement}
        projectId={projectId || ''}
        isAdmin={permissions.isAdmin}
        refreshProject={handleUserManagementSuccess}
        boards={boards.map(b => ({ id: b.id, title: b.title }))}
      />


      
      {/* Menu de opções do board */}
      <Menu
        anchorEl={boardMenuAnchor}
        open={boardMenuOpen}
        onClose={handleBoardMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={handleEditBoard}>
          <ListItemIcon>
            <EditIcon fontSize="small" color="primary" />
          </ListItemIcon>
          <ListItemText>Editar quadro</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleDeleteBoardConfirm} sx={{ color: 'error.main' }}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Excluir quadro</ListItemText>
        </MenuItem>
      </Menu>
      
      {/* Modal de confirmação para exclusão de quadro */}
      <Dialog
        open={deleteBoardConfirmOpen}
        onClose={handleConfirmDialogClose}
        aria-labelledby="delete-board-dialog-title"
        aria-describedby="delete-board-dialog-description"
      >
        <DialogTitle id="delete-board-dialog-title">
          Excluir quadro
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-board-dialog-description">
            Tem certeza que deseja excluir o quadro "{boardToDelete?.title}"? Esta ação não pode ser desfeita
            e todos os dados associados a este quadro serão perdidos permanentemente.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleConfirmDialogClose} color="primary">
            Cancelar
          </Button>
          <Button onClick={handleDeleteBoard} color="error" autoFocus>
            Excluir
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* O modal de edição do quadro agora é o mesmo que o de criação */}
    </ProjectContainer>
  );
};

export default ProjectPage;